/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component, PropsWithChildren } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import FormHeaderView from './FormHeader.view';
import SocialMediaListView from './SocialMediaList.view';
import FormFooterView from './FormFooter.view';
import { ClientOauthAccountAuthenticationMethodsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import NavButton from '../Buttons/NavButton.view';
import ClipboardView from '../Clipboard.view';

export interface Props {
	title?: string;
	textToCopy: string;
	authenticationMethods?: ClientOauthAccountAuthenticationMethodsEnum[];
	actionMenu?: JSX.Element;
	goToTableHandler?: () => void;
}

interface State {
	showCopyIcon: boolean;
}
class PersonDetailFormView extends Component<PropsWithChildren<Props>, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			showCopyIcon: false
		};
		this.showCopyIcon = this.showCopyIcon.bind(this);
		this.hideCopyIcon = this.hideCopyIcon.bind(this);
	}

	hideCopyIcon(): void {
		this.setState({
			showCopyIcon: false
		});
	}

	showCopyIcon(): void {
		this.setState({
			showCopyIcon: true
		});
	}

	render(): JSX.Element {
		const { title, authenticationMethods, actionMenu, textToCopy, goToTableHandler } = this.props;
		const { showCopyIcon } = this.state;
		const formHeader = title && (
			<FormHeaderView actionMenu={actionMenu}>
				<Box display="flex" onMouseOver={this.showCopyIcon} onMouseLeave={this.hideCopyIcon}>
					<Tooltip title={textToCopy} placement="bottom-end">
						<Typography variant="h1">{title}</Typography>
					</Tooltip>
					<ClipboardView textToCopy={textToCopy} showCopyIcon={showCopyIcon} />
				</Box>
				{authenticationMethods && <SocialMediaListView authenticationMethods={authenticationMethods} />}
			</FormHeaderView>
		);
		const formBody = this.props.children;
		const formFooter = (
			<FormFooterView>
				{goToTableHandler && <NavButton onClick={goToTableHandler}>Zur Benutzertabelle</NavButton>}
			</FormFooterView>
		);

		return (
			<Paper>
				{formHeader}
				{formBody}
				{formFooter}
			</Paper>
		);
	}
}
export default PersonDetailFormView;
